import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Role, User } from '@interfaces/user';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@services/auth.service';
import { BillingService } from '@services/billing.service';
import { DeviceTypeService } from '@services/device-type.service';
import { NavigationService } from '@services/navigation.service';
import { UrlService } from '@services/url.service';
import { UserService } from '@services/user.service';
import { UtilsService } from '@services/utils.service';
import { WebsocketService } from '@services/websocket.service';
import { WorkspaceService } from '@services/workspace.service';
import { getTranslation } from '@shared/translate-helper';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'app-main-menu',
  templateUrl: './main-menu.component.html',
  styleUrls: ['./main-menu.component.scss'],
  animations: [
    trigger('iconAnim', [
      state('collapsed', style({ paddingLeft: '22px' })),
      state('expanded', style({ paddingLeft: '30px' })),
      transition('collapsed <=> expanded', animate('200ms ease-in-out')),
    ]),
  ],
  standalone: false,
})
export class MainMenuComponent implements OnInit {
  public userProfile!: User | null;
  public loading = false;
  public fromOnboarding = false;
  public currentRouteUrl = '';
  public isSidebarExpanded = false;
  public hasSubscription = false;
  /** True when rendered inside the mobile burger drawer rather than as the sidebar. */
  @Input() drawerMode = false;
  @Output() onCloseMenuMobile = new EventEmitter<any>();

  /** The drawer is always expanded; only the desktop sidebar collapses. */
  get isExpanded(): boolean {
    return this.drawerMode || this.isSidebarExpanded;
  }

  /** Gates the admin-only Agents nav entry. */
  get isAdmin(): boolean {
    const role = this.userService.getUserData()?.role as Role;
    return [Role.ClientAdmin, Role.Admin].includes(role);
  }

  /** Navigating from the drawer should dismiss it. */
  private closeDrawer(): void {
    if (this.drawerMode) {
      this.onCloseMenuMobile.emit();
    }
  }

  constructor(
    private spinner: NgxSpinnerService,
    private authService: AuthService,
    public translateService: TranslateService,
    public userService: UserService,
    public router: Router,
    public navigationService: NavigationService,
    private billingService: BillingService,
    public deviceType: DeviceTypeService,
    private utilsService: UtilsService,
    public urlService: UrlService,
    private readonly route: ActivatedRoute,
    private readonly workspaceService: WorkspaceService,
    private readonly ws: WebsocketService,
  ) {}

  ngOnInit(): void {
    this.hasSubscription = this.userService.getHasSubscription();

    if (this.route.snapshot.queryParamMap.get('onboarding') === 'true') {
      this.fromOnboarding = true;
    }
    try {
      this.userProfile = this.userService.getUserData();

      if (!this.userService.companySubscription()) {
        this.getCompanySubscription();
      }
    } catch (error) {
      getTranslation(this.translateService, 'error_user_data').subscribe((t) => {
        this.utilsService.showAlert({
          title: t,
          alertType: 'error',
          icon: 'circle-x',
          showActionBtn: false,
          timed: true,
          time: 5000,
        });
      });
    }

    this.router.events.subscribe((event: any) => {
      if (event?.routerEvent?.url) this.currentRouteUrl = event?.routerEvent?.url;
    });
    this.workspaceService.updateWorkspaceEvent$.subscribe((name) => {
      if (this.userProfile?.company.name) this.userProfile.company.name = name;
    });
  }

  /**
   * Performs a logout action by calling the `doLogout` method of the `authService`.
   */
  getCompanySubscription(): void {
    const companyUuid = this.userService.getUserData()?.company.uuid;
    if (!companyUuid) return;
    this.loading = true;
    this.billingService
      .getCompanySubscription(companyUuid)
      .subscribe({
        next: (res) => {
          this.userService.companySubscription.set(res.subscription);
          this.userService.companySubscriptionObject.set(res);
        },
        error: () => {
          getTranslation(this.translateService, 'Error loading company subscription').subscribe((t) => {
            this.utilsService.showAlert({
              title: t,
              alertType: 'error',
              icon: 'circle-x',
              showActionBtn: false,
              timed: true,
              time: 5000,
            });
          });
        },
      })
      .add(() => (this.loading = false));
  }

  gotoPurchasePage(event: Event): void {
    event.stopPropagation();
    this.navigationService.navigateToPricingPlans();
  }

  gotoConnectorsPage(): void {
    this.navigationService.navigateToMyConnectors();
    this.closeDrawer();
  }

  gotoSpaces(): void {
    this.navigationService.navigateToSpaces();
  }

  checkLinkActive(routes: string[]): boolean {
    if (!this.currentRouteUrl) return false;
    return routes.some((route) => this.currentRouteUrl?.includes(route));
  }

  goSearch(): void {
    this.navigationService.navigateToSearchHome();
    this.closeDrawer();
  }

  goToFeedJolt(): void {
    this.userService.openFeedbackPortal();
    this.closeDrawer();
  }

  emitCloseMobileMenu(ignoreSubscriptionStatus = false) {
    if (this.hasSubscription || ignoreSubscriptionStatus) {
      this.onCloseMenuMobile.emit();
      return;
    }
  }

  navigateToSpaces() {
    this.navigationService.navigateToSpaces();
    this.closeDrawer();
  }

  navigateToAgents(): void {
    this.navigationService.navigateToAgents();
    this.closeDrawer();
  }

  closeOnboardingPopMessage() {
    this.fromOnboarding = false;
    this.router.navigate([], {
      queryParams: { onboarding: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  toggleSidebar(): void {
    this.isSidebarExpanded = !this.isSidebarExpanded;
  }

  onWorkspaceSwitched() {
    // If view is mobile, close the menu after switching workspace
    if (this.deviceType.isMobile) {
      this.onCloseMenuMobile.emit();
    }
  }
}
